import { Journey } from '@audibene/ta.utils-opportunity'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  AppointmentStatus,
  AppointmentType,
  Country,
  teleaudiologyOrTaLightJourneys,
  Route,
  RouteParameters,
  SearchParameters,
} from 'constants/'
import { useCountry, useIsSchedulingV2RedirectPending, useProviderRecommendationForAppointmentTypes, useSearchParameters } from 'hooks/'
import { useAppointments } from 'hooks/useAppointments'
import { appendSearchParamsToPathname, hydratePathname } from 'util/'

interface Props {
  appointmentType: AppointmentType
}

/**
 *
 * On schedule route redirect to consultant
    - if appointment type feature flag is false or journey is not TA

    - override above and do not redirect
        - if preview is true or
        - if country is US and user has completed/confirmed appointments
 */
const useRedirectToConsultationSchedulingFlowIfNeeded = ({ appointmentType }: Props) => {
  const searchParams = useSearchParameters()
  const country = useCountry()
  const navigate = useNavigate()
  const isAmerica = country === Country.US

  const appointmentTypeFeatureFlagMap = useProviderRecommendationForAppointmentTypes()
  const isV2RedirectPending = useIsSchedulingV2RedirectPending()

  const opportunityGid = searchParams.opportunity_gid as string | ''
  const { data: completedOrConfirmedAppointments = [], isLoading: isAppointmentsLoading } = useAppointments(
    {
      opportunity_gid: [opportunityGid],
      sort_by: 'start_date',
      sort: 'desc',
      status: [AppointmentStatus.CONFIRMED, AppointmentStatus.COMPLETED],
      type: [AppointmentType.HEARING_AID_EVALUATION],
    },
    isAmerica && Boolean(opportunityGid)
  )

  const hasCompletedOrConfirmedAppointments = completedOrConfirmedAppointments.length > 0

  useEffect(() => {
    if (isV2RedirectPending) return
    if (isAppointmentsLoading) return

    const isPreview = searchParams.preview?.toString().trim().toLowerCase() === 'true'
    const isProviderRecommendationSupportedAppointmentType = Boolean(appointmentTypeFeatureFlagMap[appointmentType])
    const isProviderRecommendationSupportedJourney = teleaudiologyOrTaLightJourneys.has(searchParams.journey as Journey)
    const isProviderRecommendationFullySupported =
      isProviderRecommendationSupportedAppointmentType && isProviderRecommendationSupportedJourney

    const shouldRedirectToConsultantSchedulingFlow =
      !isPreview && (!isAmerica || !hasCompletedOrConfirmedAppointments) && !isProviderRecommendationFullySupported

    if (shouldRedirectToConsultantSchedulingFlow) {
      const pathname = hydratePathname(Route.CONSULTANT_SCHEDULE, { [RouteParameters.COUNTRY]: country })

      const pathnameAndSearch = appendSearchParamsToPathname({
        pathname,
        searchParams: {
          ...searchParams,
          [SearchParameters.APPOINTMENT_TYPE]: appointmentType,
        },
      })

      navigate(pathnameAndSearch)
    }
  }, [
    isV2RedirectPending,
    isAppointmentsLoading,
    isAmerica,
    appointmentType,
    appointmentTypeFeatureFlagMap,
    searchParams,
    hasCompletedOrConfirmedAppointments,
    navigate,
  ])
}

export { useRedirectToConsultationSchedulingFlowIfNeeded }
